import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'react-hot-toast';
import { Lead } from '@/types';
import { api } from '@/services/api';
import { supabase } from '@/lib/supabase';
import { cleanMetaText, safeText, getCleanedFriendlyPreview } from '@/lib/utils';
import { useAuthStore } from './authStore';
import { useLojaAtivaStore } from './lojaAtivaStore';
import { notificationAllowed, claimNotification } from './notificationPreferencesStore';

// Notificação sonora (Web Audio API — sem arquivo externo)
let _audioCtx: AudioContext | null = null;

// Desbloquear AudioContext na primeira interação do usuário para evitar atraso/bloqueio pelo navegador
if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    try {
      if (!_audioCtx) {
        const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) {
          _audioCtx = new AC();
        }
      }
      if (_audioCtx) {
        if (_audioCtx.state === 'suspended') {
          _audioCtx.resume().then(() => {
            if (_audioCtx && _audioCtx.state === 'running') {
              window.removeEventListener('click', unlockAudio);
              window.removeEventListener('keydown', unlockAudio);
            }
          }).catch(() => {});
        } else if (_audioCtx.state === 'running') {
          window.removeEventListener('click', unlockAudio);
          window.removeEventListener('keydown', unlockAudio);
        }
      }
    } catch { /* silent fail */ }
  };
  window.addEventListener('click', unlockAudio, { passive: true });
  window.addEventListener('keydown', unlockAudio, { passive: true });
}

export function notifySound(): void {
  try {
    // Also gate direct callers such as Inbox's permission confirmation.
    if (!notificationAllowed(useAuthStore.getState().session?.user?.id, 'messages', 'sound')) return;

    // notifySound NÃO cria nem agenda som em contexto suspenso:
    // retornar se !_audioCtx ou state!='running'. (B1)
    if (!_audioCtx || _audioCtx.state !== 'running') {
      return;
    }
    const ctx = _audioCtx;
    const now = ctx.currentTime;
    
    // Tom 1 (descendente, tipo "ding")
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now);
    osc1.frequency.exponentialRampToValueAtTime(660, now + 0.15);
    gain1.gain.setValueAtTime(0.0001, now);
    gain1.gain.exponentialRampToValueAtTime(0.3, now + 0.02);
    gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
    osc1.connect(gain1).connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.4);
    
    // Tom 2 (mais agudo, ecoa logo depois)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1320, now + 0.12);
    gain2.gain.setValueAtTime(0.0001, now + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.22, now + 0.14);
    gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    osc2.connect(gain2).connect(ctx.destination);
    osc2.start(now + 0.12);
    osc2.stop(now + 0.5);
  } catch { /* silent fail */ }
}

const LEADS_TABLE = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_LEADS_TABLE) || 'crm_leads';
const MESSAGES_TABLE = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_MESSAGES_TABLE) || 'crm_messages';

export interface LeadFilters {
  loja_id?: string | null;
  responsavel_user_id?: string | null;
  etapa_id?: string | null;
  status?: string | null; // 'ativo' | 'ganho' | 'perdido' | status_atendimento
  busca?: string | null;
  q?: string | null;
  limit?: number;
  offset?: number;
  origem?: string | null;
  ordenacao?: 'ultima_interacao' | 'created_at' | 'nome';
  silent?: boolean;
}

const notifyBrowser = (title: string, body: string) => {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    Notification.permission !== 'granted'
  ) return;

  try {
    new Notification(title, { body });
  } catch {
    // Diagnóstico sem conteúdo privado.
  }
};

export function normalizeLeadForUI(raw: any, existing?: Lead): Lead {
  const idValue = raw.id !== undefined && raw.id !== null ? String(raw.id) : (existing?.id || '');
  
  // Ordem temporal: Evento antigo não sobrescreve preview, last_message_at ou sender mais recente (B4)
  const rawMsgTime = raw.last_message_at ? Date.parse(String(raw.last_message_at)) : NaN;
  const existingMsgTime = existing?.last_message_at ? Date.parse(String(existing.last_message_at)) : NaN;
  const isRawEventOlder = !isNaN(rawMsgTime) && !isNaN(existingMsgTime) && rawMsgTime < existingMsgTime;

  const last_message_at = isRawEventOlder
    ? (existing?.last_message_at || '')
    : (raw.last_message_at || existing?.last_message_at || raw.created_at || '');

  const ultima_interacao_at = isRawEventOlder
    ? (existing?.ultima_interacao_at || '')
    : (raw.ultima_interacao_at || existing?.ultima_interacao_at || last_message_at || raw.created_at || '');

  const unread_count = raw.unread_count !== undefined && raw.unread_count !== null
    ? Number(raw.unread_count)
    : (existing?.unread_count !== undefined ? Number(existing.unread_count) : 0);

  // FIX (P8.2): evitar recursão — só limpar se ainda tem tags/colchetes pra limpar
  let last_message_preview = isRawEventOlder
    ? (existing?.last_message_preview || '')
    : safeText(raw.last_message_preview ?? existing?.last_message_preview ?? '');
    
  if (!isRawEventOlder && last_message_preview && (
    last_message_preview.includes('[') || 
    last_message_preview.includes('<audio') || 
    last_message_preview.includes('<imagem') || 
    last_message_preview.includes('<video') ||
    last_message_preview.includes('<pdf') ||
    last_message_preview.includes('<documento')
  )) {
    last_message_preview = getCleanedFriendlyPreview(last_message_preview);
  }

  let last_message_sender_type = isRawEventOlder
    ? (existing?.last_message_sender_type || '')
    : safeText(raw.last_message_sender_type ?? existing?.last_message_sender_type ?? '');
    
  if (!isRawEventOlder && !last_message_sender_type && last_message_preview) {
    if (last_message_preview.startsWith('🤖') || last_message_preview.startsWith('IA: ') || last_message_preview.startsWith('SDR IA: ')) {
      last_message_sender_type = 'ia';
    } else if (last_message_preview.startsWith('👤') || last_message_preview.startsWith('Atendente: ')) {
      last_message_sender_type = 'humano';
    } else {
      last_message_sender_type = 'lead';
    }
  }

  let tags: string[] = [];
  const rawTags = raw.tags !== undefined ? raw.tags : (existing?.tags !== undefined ? existing.tags : null);
  if (Array.isArray(rawTags)) {
    tags = rawTags;
  } else if (typeof rawTags === 'string') {
    const trimmed = rawTags.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        tags = JSON.parse(trimmed);
      } catch {
        tags = trimmed.split(',').map((x: string) => x.trim()).filter(Boolean);
      }
    } else {
      tags = trimmed.split(',').map((x: string) => x.trim()).filter(Boolean);
    }
  } else {
    tags = [];
  }

  const nome = safeText(raw.nome ?? existing?.nome ?? 'Lead sem nome');
  const status_atendimento = safeText(raw.status_atendimento ?? existing?.status_atendimento ?? 'Novo Lead');
  const telefone = safeText(raw.telefone ?? existing?.telefone ?? '');
  const remotejid = safeText(raw.remotejid ?? existing?.remotejid ?? '');

  return {
    ...existing,
    ...raw,
    id: idValue,
    tenant_id: raw.tenant_id || existing?.tenant_id || 'default',
    loja_id: raw.loja_id !== undefined ? raw.loja_id : (existing?.loja_id ?? null),
    nome,
    telefone,
    remotejid,
    etapa_id: raw.etapa_id !== undefined ? raw.etapa_id : (existing?.etapa_id ?? null),
    responsavel_user_id: raw.responsavel_user_id !== undefined ? raw.responsavel_user_id : (existing?.responsavel_user_id ?? null),
    status_atendimento,
    last_message_at,
    ultima_interacao_at,
    unread_count,
    last_message_preview,
    last_message_sender_type: (last_message_sender_type as any) || 'lead',
    atendimento_finalizado: raw.atendimento_finalizado === true || existing?.atendimento_finalizado === true,
    tags,
    created_at: raw.created_at || existing?.created_at || new Date().toISOString(),
  } as Lead;
}

export function getLeadSortTime(lead: Partial<Lead>): number {
  const times: number[] = [];
  const parseTime = (val?: any): number | null => {
    if (!val) return null;
    const dateStr = String(val).trim();
    if (!dateStr) return null;
    const parsed = Date.parse(dateStr);
    return isNaN(parsed) ? null : parsed;
  };

  const t1 = parseTime(lead.last_message_at);
  const t2 = parseTime(lead.ultima_interacao_at);
  const t3 = parseTime(lead.created_at);

  if (t1 !== null) times.push(t1);
  if (t2 !== null) times.push(t2);
  if (t3 !== null) times.push(t3);

  if (times.length === 0) return 0;
  return Math.max(...times);
}

export function sortLeadsByActivity(leads: Lead[]): Lead[] {
  return [...leads].sort((a, b) => {
    const timeA = getLeadSortTime(a);
    const timeB = getLeadSortTime(b);
    if (timeA !== timeB) {
      return timeB - timeA;
    }
    return String(b.id || '').localeCompare(String(a.id || ''));
  });
}

export type LeadsAggregateStats = {
  total_leads: number;
  unread_count: number;
  unassigned_count: number;
  new_leads_today: number;
  new_leads_7d: number;
  new_leads_30d: number;
  new_leads_period: number;
  by_status: Record<string, number>;
  by_etapa: Record<string, number>;
  by_origem: Record<string, number>;
  by_loja: Record<string, number>;
  by_etapa_including_unassigned: Record<string, number>;
  by_etapa_tipo: Record<string, number>;
  ganhos: number;
  perdidos: number;
  qualificados: number;
  em_atendimento: number;
  sem_resposta_24h: number;
};

interface LeadsState {
  leads: Lead[];
  totalLeadsCount: number;
  aggregateStats: LeadsAggregateStats | null;
  loading: boolean;
  loadingMore: boolean;
  initialized: boolean;
  activeLeadId: string | null;
  // FIX (P0-GRUPOCASA): loja do último fetch de leads — permite refetch quando a loja ativa muda
  lastFetchedStoreId: string | null;
  fetchLeads: (filtersOrFallback?: LeadFilters | Lead[]) => Promise<void>;
  fetchMoreLeads: () => Promise<void>;
  refreshAggregateStats: (lojaId?: string, startDate?: string, endDate?: string) => Promise<void>;
  updateLeadLocally: (id: string, updates: Partial<Lead>) => void;
  appendLeads: (newLeads: Lead[]) => void;
  mergeRevalidatedLeads: (revalidatedColumnIds: string[], fetchedLeads: Lead[]) => void;
  updateMultipleLeadsLocally: (updates: {id: string, data: Partial<Lead>}[]) => void;
  removeLeadLocally: (id: string) => void;
  setActiveLeadId: (id: string | null) => void;
  refreshLeadAvatar: (id: string) => Promise<void>;
  selectedTenantIdForAlerts: string | null;
  setSelectedTenantIdForAlerts: (id: string | null) => void;
  setAlertScope: (owner: symbol, tenantId: string | null) => void; // Ação para definir dono do alerta
  releaseAlertScope: (owner: symbol) => void; // Ação para liberar dono do alerta
  stopRealtime: (clearLedger?: boolean) => void;
  ensureRealtime: () => void;
}

// Variáveis de escopo de sessão e propriedade de dono dos alertas
let realtimeEpoch = 0;
let messageAlertLedger: string[] = [];
let reconnectTimerIds: any[] = [];
let alertScopeOwner: symbol | null = null; // Dono ativo do escopo de alertas
const inFlightReads = new Map<string, Promise<{ data: any; error: any }>>(); // Uma leitura autoritativa compartilhada por identidade e época.

// Função auxiliar para verificar duplicação de alertas
function checkAndRegisterMessageAlert(msgId: string, tenantId: string, lojaId: string): boolean {
  const key = `${msgId}:${tenantId}:${lojaId}`;
  if (messageAlertLedger.includes(key)) {
    return false; // Alerta já processado
  }
  messageAlertLedger.push(key);
  if (messageAlertLedger.length > 500) {
    messageAlertLedger.shift(); // Limita o ledger na sessão atual
  }
  return true; // Alerta inédito
}

// Predicado de validação de escopo para alertas (B2)
export function acceptsScope(event: {
  tenant_id?: string | null;
  loja_id?: string | null;
}): boolean {
  const profile = useAuthStore.getState().profile;
  const { lojaAtivaId, lojasAcessiveis } = useLojaAtivaStore.getState();

  if (!profile || !event.tenant_id || !event.loja_id) return false;

  // Null loja significa todas as AUTORIZADAS carregadas, não qualquer tenant
  if (!lojasAcessiveis.some(
    l => l.id === event.loja_id &&
         l.tenant_id === event.tenant_id
  )) return false;

  if (
    profile.role !== 'super_admin' &&
    profile.tenant_id !== event.tenant_id
  ) return false;

  if (lojaAtivaId && lojaAtivaId !== event.loja_id) return false;

  return true;
}

// Função de busca de lead por ID ou JID de forma estrita (B3)
function findLeadForMessage(
  leads: Lead[],
  leadId: string | number | undefined,
  remotejid: string | undefined,
  tenantId: string | undefined,
  lojaId: string | undefined
): Lead | null {
  // Primeiro buscar por ID exato
  if (leadId) {
    const found = leads.find(l => String(l.id) === String(leadId));
    if (found) return found;
    // ID válido não carregado não pode atualizar outro contato por JID. (B3)
    return null;
  }
  // Fallback legado sem ID: JID não vazio, tenant+loja iguais e exatamente um candidato. (B3)
  if (remotejid && tenantId && lojaId) {
    const candidates = leads.filter(l =>
      l.remotejid === remotejid &&
      l.tenant_id === tenantId &&
      l.loja_id === lojaId
    );
    if (candidates.length === 1) {
      return candidates[0];
    }
  }
  return null;
}

let globalChannel: any = null;
let fetchLeadsRequestId = 0; // FIX (P2.1): guarda contra corrida de requisições
let realtimeReconnectAttempts = 0;

export const useLeadsStore = create<LeadsState>()(
  persist(
    (set, get) => ({
      leads: [],
      totalLeadsCount: 0,
      aggregateStats: null,
      loading: false,
      loadingMore: false,
      initialized: false,
      activeLeadId: null,
      lastFetchedStoreId: null,
      selectedTenantIdForAlerts: null,

      setSelectedTenantIdForAlerts: (id: string | null) => {
        set({ selectedTenantIdForAlerts: id });
      },

      setAlertScope: (owner: symbol, tenantId: string | null) => {
        alertScopeOwner = owner;
        set({ selectedTenantIdForAlerts: tenantId });
      },

      releaseAlertScope: (owner: symbol) => {
        if (alertScopeOwner !== owner) return;
        alertScopeOwner = null;
        set({ selectedTenantIdForAlerts: null });
      },

      stopRealtime: (clearLedger = false) => {
        // incrementar epoch ANTES de limpar (B4)
        realtimeEpoch++;

        // invalidar também o requestId para requisições em voo
        fetchLeadsRequestId++;

        // zerar referência do canal antes de removeChannel (B4)
        const channelToRemove = globalChannel;
        globalChannel = null;

        if (channelToRemove && supabase) {
          try {
            supabase.removeChannel(channelToRemove);
          } catch (err) {
            console.warn('[LEADS_STORE] Erro ao remover canal:', err);
          }
        }

        // cancelar reconnect/typing timers do canal (B4)
        reconnectTimerIds.forEach(clearTimeout);
        reconnectTimerIds = [];

        // invalidar mapa de leitura em voo e continuations (B4)
        inFlightReads.clear();

        // limpar ledger e dono em mudança de identidade da sessão (B4)
        if (clearLedger) {
          messageAlertLedger = [];
          alertScopeOwner = null;
          set({ selectedTenantIdForAlerts: null });
        }
      },

      ensureRealtime: () => {
        const { profile } = useAuthStore.getState();
        if (!supabase || !profile) return;

        if (globalChannel) {
          return; // Canal ativo já existente
        }

        const myEpoch = realtimeEpoch;

        globalChannel = supabase.channel('public:crm_leads_and_msgs');
        globalChannel
          .on('postgres_changes', { event: '*', schema: 'public', table: LEADS_TABLE }, payload => {
            // callbacks verificam epoch e perfil vigente (B4)
            if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;

            set(state => {
              if (payload.eventType === 'INSERT') {
                const exists = state.leads.some(l => String(l.id) === String(payload.new.id));
                if (!exists) {
                  const newLead = normalizeLeadForUI(payload.new);
                  if (!acceptsScope({ tenant_id: newLead.tenant_id, loja_id: newLead.loja_id })) return {};
                  
                  // TODOS os não-super_admin (incluindo tenant_admin) filtram por loja ativa
                  const activeLojaId = useLojaAtivaStore.getState().lojaAtivaId;
                  const profile = useAuthStore.getState().profile;
                  const isSuperAdmin = profile?.role === 'super_admin';
                  
                  if (!isSuperAdmin) {
                    if (activeLojaId && newLead.loja_id !== activeLojaId) {
                      return {}; // lead de outra loja, não adicionar
                    }
                  }
                  
                  return { leads: sortLeadsByActivity([newLead, ...state.leads]) };
                }
              }
              if (payload.eventType === 'UPDATE') {
                const updatedLeadRaw = payload.new;

                const existingLead = state.leads.find(l => String(l.id) === String(updatedLeadRaw.id));
                if (!existingLead) return {};

                // Eventos não autorizados ou de outra identidade nunca governam a lista carregada.
                const tenantId = updatedLeadRaw.tenant_id || existingLead.tenant_id;
                const lojaId = updatedLeadRaw.loja_id || existingLead.loja_id;
                if (!acceptsScope({ tenant_id: tenantId, loja_id: lojaId })) return {};
                if (tenantId !== existingLead.tenant_id || lojaId !== existingLead.loja_id) return {};
                const incomingRevision = Date.parse(updatedLeadRaw.updated_at || '');
                const currentRevision = Date.parse(existingLead.updated_at || '');
                if (Number.isFinite(incomingRevision) && Number.isFinite(currentRevision) && incomingRevision < currentRevision) return {};

                // crm_leads UPDATE: sincroniza unread_count do servidor, SEM alertar e SEM incrementar (B3)
                const normalized = normalizeLeadForUI(updatedLeadRaw, existingLead);
                const isActive = state.activeLeadId === String(normalized.id);
                const finalUnreadCount = isActive ? 0 : normalized.unread_count;
                const normalizedWithActiveUnread = { ...normalized, unread_count: finalUnreadCount };

                const updatedLeads = state.leads.map(l => String(l.id) === String(normalizedWithActiveUnread.id) 
                  ? normalizedWithActiveUnread
                  : l);

                return { leads: sortLeadsByActivity(updatedLeads) };
              }
              if (payload.eventType === 'DELETE') {
                const existing = state.leads.find(l => String(l.id) === String(payload.old.id));
                if (!existing || !acceptsScope({ tenant_id: existing.tenant_id, loja_id: existing.loja_id })) return {};
                if (payload.old.tenant_id && payload.old.tenant_id !== existing.tenant_id) return {};
                if (payload.old.loja_id && payload.old.loja_id !== existing.loja_id) return {};
                return { leads: state.leads.filter(l => String(l.id) !== String(payload.old.id)) };
              }
              return {};
            });
          })
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: MESSAGES_TABLE }, async payload => {
            // callbacks de realtime verificam epoch e perfil vigente
            if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;

            const rawMsg = payload.new;
            if (!rawMsg) return;

            // Extrair e validar o MessageID de forma estrita (B3)
            const messageId = typeof rawMsg.id === 'string' ? rawMsg.id.trim() : '';
            const canAlert = messageId.length > 0;
            if (!canAlert) {
              return; // Mensagem sem ID válido não pode gerar alerta
            }

            // Completa campos faltantes usando o lead exato correspondente já carregado (B3)
            let tenantId = rawMsg.tenant_id;
            let lojaId = rawMsg.loja_id;
            const stateLeads = get().leads;

            const leadId = rawMsg.lead_id;
            const existingLead = stateLeads.find(l => String(l.id) === String(leadId));

            if (existingLead) {
              // Se há conflito de escopo/tenant, rejeitar (B3)
              if (tenantId && existingLead.tenant_id !== tenantId) return;
              if (lojaId && existingLead.loja_id && existingLead.loja_id !== lojaId) return;

              // Preencher campos vazios a partir do lead carregado (B3)
              if (!tenantId) tenantId = existingLead.tenant_id;
              if (!lojaId) lojaId = existingLead.loja_id;
            }

            if (!tenantId || !lojaId) {
              return; // Sem tenant ou loja válidos identificados
            }

            // Aplicar acceptsScope também na sincronização autoritativa (B3)
            if (!acceptsScope({ tenant_id: tenantId, loja_id: lojaId })) {
              return;
            }

            // Dedupe por messageID+tenant+loja+sessão ANTES dos efeitos (B3)
            const isNewAlert = checkAndRegisterMessageAlert(messageId, tenantId, lojaId);
            if (!isNewAlert) {
              return; // Replay não repete alerta
            }

            // Buscar lead exato para sincronização autoritativa (B3)
            const remotejid = rawMsg.remotejid;
            let matchedLead = findLeadForMessage(stateLeads, leadId, remotejid, tenantId, lojaId);
            const reconciliationId = leadId || matchedLead?.id;
            if (!reconciliationId) return; // Sem identidade exata ou fallback legado inequívoco.

            let senderType = rawMsg.message_sender_type || (rawMsg.message_direction === 'inbound' ? 'lead' : 'humano');
            let content = rawMsg.content || '';
            let friendlyPreview = getCleanedFriendlyPreview(content) || 'Mensagem recebida';
            const msgTime = rawMsg.created_at || '';

            // INSERT não faz unread_count += 1 localmente (B3)
            // Reconciliar por SELECT/UPDATE do banco de forma coalescida e protegida por updated_at (generation)
            if (reconciliationId) {
              try {
                const readKey = JSON.stringify([myEpoch, tenantId, lojaId, reconciliationId]);
                let read = inFlightReads.get(readKey);
                if (!read) {
                  // O mapa compartilha apenas a leitura em voo, nunca estado durável paralelo.
                  const operation = Promise.resolve(supabase
                    .from('crm_leads')
                    .select('*')
                    .eq('id', reconciliationId)
                    .eq('tenant_id', tenantId)
                    .eq('loja_id', lojaId)
                    .maybeSingle());
                  read = operation.finally(() => {
                    if (inFlightReads.get(readKey) === read) inFlightReads.delete(readKey);
                  });
                  inFlightReads.set(readKey, read);
                }
                const { data: updatedLead, error: readError } = await read;

                // Revalidar autorização, época e identidade depois de cada transporte.
                if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;
                if (!acceptsScope({ tenant_id: tenantId, loja_id: lojaId })) return;
                if (readError) return;
                if (updatedLead && (String(updatedLead.id) !== String(reconciliationId) || updatedLead.tenant_id !== tenantId || updatedLead.loja_id !== lojaId)) return;

                if (updatedLead) {
                  matchedLead = normalizeLeadForUI(updatedLead, matchedLead || undefined);
                  set(state => {
                    const currentLeads = state.leads;
                    const existing = currentLeads.find(l => String(l.id) === String(reconciliationId));
                    
                    // Geração/timestamp check para evitar revisões atrasadas (B3)
                    const serverTime = updatedLead.updated_at ? Date.parse(updatedLead.updated_at) : 0;
                    const localTime = existing?.updated_at ? Date.parse(existing.updated_at) : 0;
                    if (Number.isFinite(localTime) && Number.isFinite(serverTime) && serverTime < localTime) {
                      return {};
                    }

                    const normalized = normalizeLeadForUI(updatedLead, existing);
                    const isActive = state.activeLeadId === String(normalized.id);
                    const finalUnreadCount = isActive ? 0 : normalized.unread_count;

                    // A leitura autoritativa governa o resumo; evento antigo não regride a ordenação.
                    const normalizedWithActiveUnread = {
                      ...normalized,
                      unread_count: finalUnreadCount
                    };

                    const updatedLeads = existing
                      ? currentLeads.map(l => String(l.id) === String(normalizedWithActiveUnread.id) ? normalizedWithActiveUnread : l)
                      : [...currentLeads, normalizedWithActiveUnread];
                    return { leads: sortLeadsByActivity(updatedLeads) };
                  });
                }
              } catch (err) {
                console.warn('[LEADS_STORE] Falha na reconciliação do lead.');
                return;
              }
            }

            if (!matchedLead || realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;
            if (!acceptsScope({ tenant_id: tenantId, loja_id: lojaId })) return;


            // Alertas apenas para inbound, sender lead, público e não deletado
            const isInbound = rawMsg.message_direction === 'inbound';
            const isSenderLead = senderType === 'lead';
            const isPublic = rawMsg.is_private !== true;
            const isNotDeleted = rawMsg.is_deleted !== true;

            if (isInbound && isSenderLead && isPublic && isNotDeleted) {
              // Som, toast e desktop exigem acceptsScope e selectedTenantIdForAlerts (B2)
              const isAlertAllowed = acceptsScope({ tenant_id: tenantId, loja_id: lojaId }) &&
                (!get().selectedTenantIdForAlerts || get().selectedTenantIdForAlerts === tenantId);

              if (isAlertAllowed) {
                const chatActive = get().activeLeadId === String(matchedLead.id);
                const msgTimeParsed = msgTime ? Date.parse(msgTime) : NaN;
                const nowTime = Date.now();
                const ageSeconds = !isNaN(msgTimeParsed) ? (nowTime - msgTimeParsed) / 1000 : -1;
                
                // Idade entre 0 e 60s (política de supressão de alerta retroativo - B3)
                const isRecent = ageSeconds >= 0 && ageSeconds <= 60;

                if (!chatActive && isRecent) {
                  const identity = useAuthStore.getState().session?.user?.id;
                  if (!identity || !claimNotification(identity, 'messages', String(rawMsg.id || ''))) return;
                  if (notificationAllowed(identity, 'messages', 'sound')) notifySound();
                  if (notificationAllowed(identity, 'messages', 'toast')) toast(`Mensagem de ${matchedLead.nome || matchedLead.telefone || 'Lead'}`, {
                    icon: '🟢',
                    position: 'top-right',
                    duration: 4000
                  });
                  if (notificationAllowed(identity, 'messages', 'desktop')) notifyBrowser(`🔔 ${matchedLead.nome || matchedLead.telefone || 'Novo'}`, friendlyPreview);
                }
              }
            }
          })
          .on('broadcast', { event: 'typing' }, payload => {
            if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;

            const { remotejid, is_typing } = payload.payload || {};
            if (!remotejid) return;
            set(state => {
              const updatedLeads = state.leads.map(lead => {
                if (lead.remotejid === remotejid || remotejid.includes(lead.remotejid) || lead.remotejid.includes(remotejid)) {
                  return { ...lead, is_typing: !!is_typing };
                }
                return lead;
              });
              return { leads: updatedLeads };
            });
            
            if (is_typing) {
              const tId = setTimeout(() => {
                if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;
                set(state => {
                  const updatedLeads = state.leads.map(lead => {
                    if ((lead.remotejid === remotejid || remotejid.includes(lead.remotejid) || lead.remotejid.includes(remotejid)) && lead.is_typing) {
                      return { ...lead, is_typing: false };
                    }
                    return lead;
                  });
                  return { leads: updatedLeads };
                });
              }, 5000);
              reconnectTimerIds.push(tId);
            }
          })
          .subscribe((status: string) => {
            if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;

            if (status === 'SUBSCRIBED') {
              console.log('[LEADS_STORE] Canal realtime conectado');
              realtimeReconnectAttempts = 0;
            } else if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && realtimeReconnectAttempts < 5) {
              realtimeReconnectAttempts++;
              const delay = Math.min(1000 * Math.pow(2, realtimeReconnectAttempts), 60000);
              console.warn(`[LEADS_STORE] Canal realtime caiu (status=${status}), reconectando em ${delay/1000}s... (Tentativa ${realtimeReconnectAttempts}/5)`);
              
              const tId = setTimeout(() => {
                if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;
                const channelToRemove = globalChannel;
                globalChannel = null;
                if (channelToRemove) {
                  try {
                    supabase?.removeChannel(channelToRemove);
                  } catch (e) {
                    console.warn('[LEADS_STORE] Erro ao remover canal (ignorado):', e);
                  }
                }
                get().fetchLeads();
              }, delay);
              reconnectTimerIds.push(tId);
            }
          });
      },

      refreshAggregateStats: async (lojaId?: string, startDate?: string, endDate?: string) => {
        const myEpoch = realtimeEpoch;
        try {
          const stats = await api.getLeadsAggregateStats({
            loja_id: lojaId,
            start_date: startDate,
            end_date: endDate
          });
          if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;
          if (stats) {
            set({ aggregateStats: stats });
          }
        } catch (e) {
          console.warn('[LEADS_STORE] refreshAggregateStats failed:', e);
        }
      },

  fetchLeads: async (filtersOrFallback) => {
    get().ensureRealtime(); // ensureRealtime não pode ficar depois do earlyreturn por cache (B4)
    const myEpoch = realtimeEpoch; // Captura do epoch no início do trabalho assíncrono (B4)
    let filters: LeadFilters | undefined = undefined;
    let fallback: Lead[] | undefined = undefined;

    if (Array.isArray(filtersOrFallback)) {
      fallback = filtersOrFallback;
    } else if (filtersOrFallback && typeof filtersOrFallback === 'object') {
      filters = filtersOrFallback;
    }

    // PERFORMANCE: se já tem leads carregados e o filtro de loja não mudou, NÃO refetch
    // (a menos que seja uma busca explícita com q= ou busca=)
    const currentLeads = get().leads;
    const activeStoreId = useLojaAtivaStore.getState().lojaAtivaId;
    const filterStoreId = filters?.loja_id || activeStoreId;
    const hasSearch = filters?.q || filters?.busca;
    // FIX (P0-GRUPOCASA): se a LOJA ATIVA mudou desde o último fetch, SEMPRE refetch.
    const storeChanged = String(filterStoreId || '') !== String(get().lastFetchedStoreId || '');
    // Pedido de página (Lista de Leads manda offset explícito): sempre busca e SUBSTITUI a lista
    // pela página do servidor. Antes offset>0 concatenava e a página 1 caía no cache abaixo.
    const isPageRequest = filters?.offset !== undefined;
    if (!hasSearch && !storeChanged && !isPageRequest && currentLeads.length > 0 && filters?.silent !== false) {
      // Mantém os leads atuais, só atualiza aggregateStats em background
      get().refreshAggregateStats(filterStoreId || undefined);
      return;
    }
    // Só quem realmente vai buscar invalida a requisição em voo; o retorno por cache acima
    // (ex.: sync periódico do Layout) não pode descartar a página que a lista está carregando.
    const myRequestId = ++fetchLeadsRequestId;

    const isSilent = filters?.silent || (get().initialized && !filters?.busca);
    if (!isSilent) {
      set({ loading: true });
    }

    try {
      let fetched: Lead[] = [];
      let fetchedTotalCount = 0;
      let success = false;

      // 1. First try Express API (/api/leads) which bypasses RLS issues and handles tenant/loja correctly
      try {
        const apiParams: any = {};
        if (filters?.loja_id) apiParams.loja_id = filters.loja_id;
        if (filters?.responsavel_user_id) apiParams.responsavel_user_id = filters.responsavel_user_id;
        if (filters?.etapa_id) apiParams.etapa_id = filters.etapa_id;
        const searchQuery = filters?.q || filters?.busca;
        if (searchQuery) apiParams.q = searchQuery;
        if (filters?.offset !== undefined) apiParams.offset = filters.offset;
        apiParams.limit = filters?.limit ?? 200;

        const apiRes = await api.getLeads(apiParams);

        // Guardas pós-await obrigatórias (B4)
        if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile || myRequestId !== fetchLeadsRequestId) return;

        const apiData = Array.isArray(apiRes) ? apiRes : apiRes.data;
        const totalCount = typeof apiRes === 'object' && 'totalCount' in apiRes ? apiRes.totalCount : apiData.length;

        if (Array.isArray(apiData)) {
          fetched = apiData.map(l => normalizeLeadForUI(l));
          fetchedTotalCount = totalCount;
          success = true;
        }
      } catch (apiErr) {
        // Guardas pós-await obrigatórias (B4)
        if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile || myRequestId !== fetchLeadsRequestId) return;
        console.warn('[LEADS_STORE] api.getLeads failed, falling back to Supabase client:', apiErr);
      }

      // 2. Fallback to Supabase client query if Express API didn't return
      if (!success && supabase) {
        let query = supabase
          .from('crm_leads')
          .select('*', { count: 'exact' });

        const activeStoreId = useLojaAtivaStore.getState().lojaAtivaId;
        const filterStoreId = filters?.loja_id || activeStoreId;
        if (filterStoreId) {
          query = query.eq('loja_id', filterStoreId);
        }

        if (filters?.responsavel_user_id) {
          query = query.eq('responsavel_user_id', filters.responsavel_user_id);
        }
        if (filters?.etapa_id) {
          query = query.eq('etapa_id', filters.etapa_id);
        }
        if (filters?.origem) {
          query = query.eq('origem', filters.origem);
        }
        const searchQuery = filters?.q || filters?.busca;
        if (searchQuery) {
          const lower = searchQuery.trim();
          query = query.or(`nome.ilike.%${lower}%,telefone.ilike.%${lower}%,email.ilike.%${lower}%`);
        }

        query = query
          .order('created_at', { ascending: false, nullsFirst: false })
          .order('id', { ascending: true });

        const limitVal = filters?.limit ?? 200;
        const offsetVal = filters?.offset ?? 0;
        query = query.range(offsetVal, offsetVal + limitVal - 1);

        const { data, error, count } = await query;

        // Guardas pós-await obrigatórias (B4)
        if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile || myRequestId !== fetchLeadsRequestId) return;

        if (error) {
          console.error('[LEADS_STORE] Error fetching crm_leads:', error);
          if (fallback) set({ leads: fallback });
        } else if (data) {
          fetched = data.map(l => normalizeLeadForUI(l));
          fetchedTotalCount = count ?? data.length;
          success = true;
        }
      }

      if (success) {
        if (myRequestId !== fetchLeadsRequestId) return;
        
        const currentLeads = get().leads;
        let merged: Lead[];
        if (isPageRequest) {
          merged = fetched;
        } else {
          // Retain any leads currently in state that were created in the last 60s
          const sixtySecondsAgo = Date.now() - 60000;
          const recentLocalLeads = currentLeads.filter(l => {
            const createdAt = new Date(l.created_at).getTime();
            return createdAt > sixtySecondsAgo && !fetched.some(f => String(f.id) === String(l.id));
          });
          merged = sortLeadsByActivity([...recentLocalLeads, ...fetched]);
        }

        set({ leads: merged, totalLeadsCount: fetchedTotalCount, initialized: true, lastFetchedStoreId: filterStoreId || null });
      } else if (fallback) {
        set({ leads: fallback });
      }
    } catch (err) {
      if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile || myRequestId !== fetchLeadsRequestId) return;
      console.error('[LEADS_STORE] Exception:', err);
      if (fallback) set({ leads: fallback });
    } finally {
      // finally antigo não altera loading/initialized de sessão nova (B4)
      if (realtimeEpoch === myEpoch && useAuthStore.getState().profile && myRequestId === fetchLeadsRequestId) {
        set({ loading: false, initialized: true });
      }
    }

    // Chamar o ensureRealtime consolidado de gerenciamento de sessão se o contexto for atual (B4)
    if (realtimeEpoch === myEpoch && useAuthStore.getState().profile && myRequestId === fetchLeadsRequestId) {
      get().ensureRealtime();
    }
  },

  updateLeadLocally: (id, updates) => {
    set(state => {
      const updatedLeads = state.leads.map(l => {
        if (l.id === id) {
          return normalizeLeadForUI(updates, l);
        }
        return l;
      });
      return { leads: sortLeadsByActivity(updatedLeads) };
    });
  },

  appendLeads: (newLeads: Lead[]) => {
    if (!newLeads || newLeads.length === 0) return;
    set(state => {
      const existingIds = new Set(state.leads.map(l => String(l.id)));
      const toAdd = newLeads.filter(l => !existingIds.has(String(l.id)));
      if (toAdd.length === 0) return {};
      return {
        leads: sortLeadsByActivity([...state.leads, ...toAdd])
      };
    });
  },

  mergeRevalidatedLeads: (revalidatedColumnIds: string[], fetchedLeads: Lead[]) => {
    set(state => {
      const colIdSet = new Set(revalidatedColumnIds.map(id => String(id)));
      const fetchedMap = new Map<string, Lead>();
      fetchedLeads.forEach(l => {
        if (l && l.id !== undefined && l.id !== null) {
          fetchedMap.set(String(l.id), l);
        }
      });

      const now = Date.now();
      const existingIds = new Set<string>();
      const preservedLeads: Lead[] = [];

      for (const lead of state.leads) {
        const leadIdStr = String(lead.id);
        existingIds.add(leadIdStr);

        if (fetchedMap.has(leadIdStr)) {
          // Versão do servidor substitui versão local por ID
          preservedLeads.push(fetchedMap.get(leadIdStr)!);
        } else {
          // Lead não veio no resultado do servidor
          const createdAtTime = lead.created_at ? new Date(lead.created_at).getTime() : 0;
          const isRecent = createdAtTime > 0 && !isNaN(createdAtTime) && (now - createdAtTime < 60000);
          const leadColId = lead.etapa_id ? String(lead.etapa_id) : '';
          const belongsToRevalidatedCol = colIdSet.has(leadColId);

          if (isRecent || !belongsToRevalidatedCol) {
            // Preserva se for criado há <60s ou se pertencer a uma coluna não revalidada
            preservedLeads.push(lead);
          }
          // Se pertencia à coluna revalidada e não veio no fetch e não é recente, remove
        }
      }

      // Adiciona novos leads que não estavam no store
      const newLeads: Lead[] = [];
      for (const fetchedLead of fetchedLeads) {
        const fetchedIdStr = String(fetchedLead.id);
        if (!existingIds.has(fetchedIdStr)) {
          newLeads.push(fetchedLead);
        }
      }

      return {
        leads: sortLeadsByActivity([...preservedLeads, ...newLeads])
      };
    });
  },

  updateMultipleLeadsLocally: (updatesList) => {
    set(state => {
      let newLeads = [...state.leads];
      updatesList.forEach(update => {
        newLeads = newLeads.map(l => l.id === update.id ? normalizeLeadForUI(update.data, l) : l);
      });
      return { leads: sortLeadsByActivity(newLeads) };
    });
  },

  removeLeadLocally: (id) => {
    set(state => ({
      leads: state.leads.filter(l => l.id !== id),
      activeLeadId: state.activeLeadId === id ? null : state.activeLeadId
    }));
  },

  setActiveLeadId: (id) => set({ activeLeadId: id }),

  refreshLeadAvatar: async (id) => {
    const myEpoch = realtimeEpoch; // Captura do epoch (B4)
    try {
      const result = await api.refreshAvatar(id, true);
      // Guardas pós-await (B4)
      if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;

      set(state => ({
        leads: state.leads.map(l => l.id === id ? { ...l, ...result } : l)
      }));
      if (result.status === 'updated') {
        toast.success('Foto atualizada com sucesso');
      } else if (result.status === 'without_photo') {
        toast.success('Sem foto pública disponível');
      }
    } catch (err: any) {
      console.warn('Refresh avatar failed silently:', err);
    }
  },

  fetchMoreLeads: async () => {
    const state = get();
    if (state.loadingMore || (state.totalLeadsCount > 0 && state.leads.length >= state.totalLeadsCount)) {
      return;
    }
    const myEpoch = realtimeEpoch; // Captura do epoch (B4)
    const activeStoreId = useLojaAtivaStore.getState().lojaAtivaId;
    const currentOffset = state.leads.length;
    const limit = 200;

    try {
      set({ loadingMore: true });
      const res = await api.getLeads({
        loja_id: activeStoreId || undefined,
        offset: currentOffset,
        limit
      });
      // Guardas pós-await (B4)
      if (realtimeEpoch !== myEpoch || !useAuthStore.getState().profile) return;

      const apiData = Array.isArray(res) ? res : res.data;
      const totalCount = typeof res === 'object' && 'totalCount' in res ? res.totalCount : state.totalLeadsCount;

      if (Array.isArray(apiData) && apiData.length > 0) {
        const normalized = apiData.map(l => normalizeLeadForUI(l));
        state.appendLeads(normalized);
        set({ totalLeadsCount: totalCount });
      }
    } catch (err) {
      console.warn('[LEADS_STORE] fetchMoreLeads falhou:', err);
    } finally {
      // finally antigo não altera loading/initialized de sessão nova (B4)
      if (realtimeEpoch === myEpoch && useAuthStore.getState().profile) {
        set({ loadingMore: false });
      }
    }
  }
    }),
    {
      name: 'task-crm-leads-ui',
      partialize: (state) => ({
        activeLeadId: state.activeLeadId
      })
    }
  )
);
